import { PageController } from '@client/components/_core/layout/PageController';
import { Emails } from '@client/components/_shared/email/Emails';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { Loading } from '@client/components/_ui/feedback/Loading';
import { useTrackEngagementView } from '@client/hooks/business/engagement/useTrackEngagementView';
import { useOrgId } from '@client/hooks/business/org/useOrgId';
import { useUser } from '@client/hooks/business/user/useUser';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { useFeatureFlag, useFeatureFlagSettlement } from '@client/hooks/useFeatureFlag';
import { ENABLE_EMAIL_WORKLOAD_MANAGER_FLAG } from '@shared/constants/business/communication';
import { createFileRoute } from '@tanstack/react-router';
import { zodValidator } from '@tanstack/zod-adapter';
import { Result } from 'antd';
import { RefreshCw } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { z } from 'zod';

const searchSchema = z.object({
    selectedId: z.coerce.bigint().optional(),
});

export const Route = createFileRoute('/console/$orgId/workload-manager/emails')({
    staticData: {
        metadata: {
            title: 'Emails',
        },
        ui: {
            heightVariant: 'fit',
        },
    },
    validateSearch: zodValidator(searchSchema),
    component: EmailsRouteGuard,
});

/**
 * The flag is read in render rather than in `beforeLoad`: `getFlag` answers with the default
 * before PostHog has evaluated flags for the signed-in user and organisation, so a cold deep link
 * would be bounced to Tasks (losing `selectedId`) in an organisation that has Emails enabled.
 * Wait until evaluation settles for this identity, then either mount Emails or redirect.
 *
 * The redirect cannot be undone, so it only follows a real evaluation. When the wait runs out
 * with nothing evaluated (slow or blocked PostHog), the flag still reads as its default: show a
 * notice instead. A later successful delivery is an evaluation (`posthogClient` publishes it), so
 * the guard then mounts Emails with the same `selectedId`, or redirects if the flag is off.
 */
function EmailsRouteGuard() {
    const orgId = useOrgId();
    const navigate = useNavigate();
    const user = useUser({ throwOnUndefined: false });
    const settlement = useFeatureFlagSettlement({ userId: user?.id.toString(), orgId: orgId.toString() });
    const emailsEnabled = useFeatureFlag(ENABLE_EMAIL_WORKLOAD_MANAGER_FLAG, false);
    const shouldRedirect = settlement === 'evaluated' && !emailsEnabled;

    const navigateRef = useRef(navigate);
    navigateRef.current = navigate;
    useEffect(() => {
        if (shouldRedirect) {
            navigateRef.current({ to: '/console/$orgId/workload-manager/tasks', params: { orgId }, replace: true });
        }
    }, [shouldRedirect, orgId]);

    if (emailsEnabled) {
        return <EmailsRoute />;
    }
    if (settlement === 'pending') {
        return <Loading />;
    }
    return settlement === 'timed-out' ? <EmailsUnconfirmed orgId={orgId} /> : null;
}

/** Feature flags could not be evaluated in time: let the user retry or move on, never redirect. */
function EmailsUnconfirmed({ orgId }: { orgId: bigint }) {
    return (
        <Result
            status="warning"
            title="Emails could not be confirmed"
            subTitle="We could not check whether Emails is turned on for this organisation right now. Retry, or go to Tasks."
            extra={[
                <Button key="retry" type="primary" icon={<RefreshCw />} onClick={reloadPage}>
                    Retry
                </Button>,
                <Button.Link
                    key="tasks"
                    variant="secondary"
                    to="/console/$orgId/workload-manager/tasks"
                    params={{ orgId }}
                >
                    Go to Tasks
                </Button.Link>,
            ]}
        />
    );
}

/** A page load runs the whole identify and flag evaluation again and keeps the URL. */
function reloadPage() {
    window.location.reload();
}

function EmailsRoute() {
    useTrackEngagementView('workload_tab_viewed', { tab: 'emails' });
    const navigate = useNavigate();
    const orgId = useOrgId();

    const { selectedId: urlSelectedId } = Route.useSearch();
    const [localSelectedId, setLocalSelectedId] = useState<bigint | undefined>();

    useEffect(() => {
        if (urlSelectedId !== localSelectedId) {
            setLocalSelectedId(urlSelectedId);
        }
    }, [urlSelectedId]);

    const handleSelectedIdChange = useCallback(
        (id: bigint | undefined) => {
            setLocalSelectedId(id);
            navigate({
                to: '/console/$orgId/workload-manager/emails',
                params: { orgId },
                search: { selectedId: id },
            });
        },
        [navigate, orgId],
    );

    return (
        <PageController route={Route}>
            <Emails selectedId={localSelectedId} onSelectedIdChange={handleSelectedIdChange} />
        </PageController>
    );
}
